import React from "react";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import html2canvas from 'html2canvas';
import styled from 'styled-components';

// Convert image URL to Data URL
const toDataURL = url => fetch(url)
  .then(res => res.blob())
  .then(blob => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  }));

// Banner styles
const Banner = styled.div`
  padding: 0.5rem 1rem;
  margin: 0 0 1.5rem 0;
  border-radius: 8px;
  background: linear-gradient(90deg, #E31B23, #FFD700, #006E33);
  color: white;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  h2 {
    margin: 0 0 2px 0;
    font-size: 1.1rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    line-height: 1.3;
    
    @media (max-width: 768px) {
      font-size: 1rem;
      flex-wrap: wrap;
      gap: 0.25rem 0.5rem;
    }
  }
  
  .divider {
    opacity: 0.7;
    font-weight: 300;
    @media (max-width: 768px) {
      display: inline;
    }
  }
  
  .promo-text {
    font-size: 0.75rem;
    opacity: 0.9;
    margin-top: 0.2rem;
    display: block;
    font-style: italic;
    line-height: 1.3;
    
    @media (max-width: 480px) {
      font-size: 0.7rem;
    }
  }
  
  .highlight {
    font-weight: 600;
    color: #FFD700;
    text-shadow: 0 0 2px rgba(0,0,0,0.3);
  }
`;

// Toolbar and action button styles
const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 0.5rem 0 2rem;
`;
const ActionButton = styled.button`
  background: ${props => props.bg};
  color: #fff;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: ${props => props.hoverBg}; }
`;

export default function ExportShare({ days }) {
  const [showReviewModal, setShowReviewModal] = React.useState(false);
  const [reviewSubmitted, setReviewSubmitted] = React.useState(false);

  const handleReviewSubmit = () => {
    // Here you would typically make an API call to submit the review
    // For now, we'll just mark it as submitted
    setReviewSubmitted(true);
    setShowReviewModal(false);
  };

  const handleReviewSkip = () => {
    setShowReviewModal(false);
  };

  // Function to generate Mapbox static map URL
  const generateMapUrl = (items) => {
    // Filter items with coordinates
    const points = [];
    items.forEach(item => {
      if (item.lat && item.lng) {
        points.push({
          coordinates: [item.lng, item.lat],
          title: item.title || 'Location'
        });
      }
    });

    if (points.length === 0) return null;

    // Calculate bounds for the map
    const lngs = points.map(p => p.coordinates[0]);
    const lats = points.map(p => p.coordinates[1]);
    const bounds = [
      [Math.min(...lngs) - 0.01, Math.min(...lats) - 0.01],
      [Math.max(...lngs) + 0.01, Math.max(...lats) + 0.01]
    ];

    // Create GeoJSON for the markers only
    const geojson = {
      type: 'FeatureCollection',
      features: points.map((point, i) => ({
        type: 'Feature',
        properties: {
          'marker-color': '#E31B23',
          'marker-symbol': i + 1,
          title: point.title
        },
        geometry: {
          type: 'Point',
          coordinates: point.coordinates
        }
      }))
    };

    // Generate Mapbox static image URL
    const accessToken = 'pk.eyJ1IjoidHJhdmVscG9ydHVnYWxjbHViIiwiYSI6ImNtOTZ6czVtNjAxcTMybXNpbXJnNGxwZXcifQ.lLXCMGzteF-T-x3aPGB3nw';
    const padding = 20;
    const width = 800;
    const height = 400;
    
    const boundsParam = `${bounds[0][0]},${bounds[0][1]},${bounds[1][0]},${bounds[1][1]}`;
    const geojsonStr = encodeURIComponent(JSON.stringify(geojson));
    
    return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/geojson(${geojsonStr})/auto/${width}x${height}@2x?padding=${padding}&access_token=${accessToken}`;
  };

  // Function to add footer to each page
  const addFooter = (doc) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    
    // Add footer text
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    
    // Left-aligned text
    doc.text('Your Personal Itinerary', margin, pageHeight - 15);
    
    // Right-aligned text
    const date = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    doc.text(`Generated on ${date}`, pageWidth - margin, pageHeight - 15, { align: 'right' });
    
    // Add page number if more than one page
    const pageCount = doc.internal.getNumberOfPages();
    if (pageCount > 1) {
      doc.text(
        `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 15,
        { align: 'center' }
      );
    }
  };
  
  // Function to add header with logo and trip info
  const addHeader = async (doc, dayNumber, dayLabel) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let y = 20;
    
    // Add logo (left-aligned)
    try {
      const logoUrl = 'https://www.portugaldaytrip.com/logo.png'; // Replace with your logo URL
      const logoData = await toDataURL(logoUrl);
      doc.addImage(logoData, 'PNG', margin, y, 120, 40);
    } catch (error) {
      console.error('Error loading logo:', error);
      // Fallback to text
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(10, 110, 156);
      doc.text('PORTUGAL DAY TRIP', margin, y + 10);
    }
    
    // Add trip title (right-aligned)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(10, 110, 156);
    doc.text('Your Portugal Itinerary', pageWidth - margin, y + 10, { align: 'right' });
    
    // Add day info
    y += 30;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text(`Day ${dayNumber}: ${dayLabel}`, margin, y);
    
    // Add date (example format - adjust as needed)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    const date = new Date();
    date.setDate(date.getDate() + (dayNumber - 1));
    const dateStr = date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric' 
    });
    doc.text(dateStr, margin, y + 6);
    
    // Add divider line
    y += 15;
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageWidth - margin, y);
    
    return y + 20; // Return Y position after header
  };

  const exportPDF = async () => {
    if (!reviewSubmitted) {
      setShowReviewModal(true);
      return;
    }

    // Create PDF with custom font
    const doc = new jsPDF({ 
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    const margin = 20;
    
    // Initialize first page properly
    if (doc.internal.getNumberOfPages() === 0) {
      doc.addPage();
    } else {
      // If there are existing pages, remove them and add a fresh one
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = pageCount; i > 0; i--) {
        doc.deletePage(i);
      }
      doc.addPage();
    }
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - margin * 2;
    
    // Define colors from theme.js
    const primaryColor = [10, 110, 156];     // #0A6E9C - primary
    const secondaryColor = [255, 123, 84];   // #FF7B54 - secondary
    const accentColor = [255, 208, 86];      // #FFD056 - accent
    const textColor = [26, 32, 44];          // #1A202C - text
    const lightTextColor = [113, 128, 150];  // #718096 - muted
    const cardColor = [255, 255, 255];       // #FFFFFF - card
    const backgroundColor = [249, 250, 251]; // #F9FAFB - background
    const borderColor = [226, 232, 240];     // #E2E8F0 - border
    const successColor = [56, 161, 105];     // #38A169 - success
    
    for (let idx = 0; idx < days.length; idx++) {
      const day = days[idx];
      if (!day) continue;
      
      // Add new page for each day (except first day)
      if (idx > 0) {
        doc.addPage();
      }
      
      // Add header with logo and day title
      let y = await addHeader(doc, idx + 1, day.label);
      
      // Add some space after header
      y += 10;
      
      // Ensure we have enough space for content
      if (y > doc.internal.pageSize.getHeight() - 60) {
        doc.addPage();
        y = margin + 40;
        y = await addHeader(doc, idx + 1, day.label);
        y += 10;
      }
      
      // Date and generated info
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(...lightTextColor);
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })}`, margin, y);
      y += 8;
      
      // Get all activities for the day
      const activities = [];
      const slots = day.slots || { morning: day.items || [] };
      Object.values(slots).forEach(slot => {
        if (Array.isArray(slot)) {
          activities.push(...slot);
        }
      });
      
      // Filter activities with coordinates
      const activitiesWithCoords = activities.filter(a => a.lat && a.lng);
      
      // Generate and add map if there are locations
      if (activitiesWithCoords.length > 0) {
        try {
          const mapUrl = generateMapUrl(activitiesWithCoords);
          if (mapUrl) {
            // Add map title with Playfair Display style
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(14);
            doc.setTextColor(...primaryColor);
            doc.text('Your Day Trip Map', margin, y);
            y += 8;
            
            // Add map with card-like styling
            const mapHeight = 110;
            const imgData = await toDataURL(mapUrl);
            
            // Draw a card background for the map (white with subtle shadow effect)
            doc.setFillColor(...cardColor);
            doc.setDrawColor(...borderColor);
            doc.roundedRect(margin - 2, y - 2, contentWidth + 4, mapHeight + 4, 4, 4, 'FD');
            
            // Add the map image
            doc.addImage(imgData, 'PNG', margin, y, contentWidth, mapHeight, undefined, 'FAST');
            y += mapHeight + 12;
            
            // Add map legend with card styling
            doc.setFillColor(...cardColor);
            doc.setDrawColor(...borderColor);
            const legendHeight = 10 + activitiesWithCoords.length * 5;
            doc.roundedRect(margin, y, contentWidth, legendHeight, 4, 4, 'FD');
            
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(...primaryColor);
            doc.text('Map Legend', margin + 8, y + 8);
            y += 12;
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            activitiesWithCoords.forEach((activity, i) => {
              if (y > doc.internal.pageSize.getHeight() - 30) {
                doc.addPage();
                y = margin + 40; // Leave space for header
                addHeader(doc, idx + 1, day.label);
              }
              doc.setTextColor(...textColor);
              doc.text(`${i + 1}. ${activity.title}`, margin + 8, y);
              y += 5;
            });
            y += 10;
          }
        } catch (error) {
          console.error('Error generating map:', error);
          // Continue without map if there's an error
        }
      }
      
      // Schedule section with styled header - using Playfair Display style
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...primaryColor);
      doc.text('Daily Itinerary', margin, y);
      y += 10;
      
      // Schedule items
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      
      for (const [slotKey, items] of Object.entries(slots)) {
        if (!items || !Array.isArray(items) || items.length === 0) continue;
        
        const slotLabel = {
          morning: 'Morning',
          afternoon: 'Afternoon',
          evening: 'Evening',
          night: 'Night'
        }[slotKey] || slotKey;
        
        // Time slot colors based on planner theme
        const slotColors = {
          morning: accentColor,       // #FFD056 - accent color for morning
          afternoon: secondaryColor,  // #FF7B54 - secondary color for afternoon
          evening: primaryColor,      // #0A6E9C - primary color for evening
          night: [128, 90, 213]       // #805AD5 - lavender color for night
        };
        
        const slotColor = slotColors[slotKey] || primaryColor;
        
        // Draw slot header with card-like styling
        doc.setFillColor(...cardColor);
        doc.setDrawColor(...borderColor);
        doc.roundedRect(margin, y, contentWidth, 8, 4, 4, 'FD');
        
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...slotColor);
        doc.text(slotLabel, margin + 8, y + 6);
        y += 12;
        
        // Add activities for this slot
        doc.setFont('helvetica', 'normal');
        // Process items sequentially to handle async operations
        for (const item of items) {
          // --- Begin Activity Card ---
          const circleRadius = 12;
          const cardLeftMargin = margin + 30; // Space for time circle
          let contentHeight = 0;
          
          // Calculate content height
          contentHeight += 8; // Top padding
          contentHeight += 6; // Title (1 line)
          
          // Description (max 2 lines)
          if (item.desc) {
            const splitDesc = doc.splitTextToSize(item.desc, contentWidth - cardLeftMargin - 10);
            contentHeight += Math.min(splitDesc.length, 2) * 5;
          }
          
          // Location (1 line)
          if (item.address) contentHeight += 5;
          contentHeight += 8; // Bottom padding
          contentHeight = Math.max(contentHeight, 30); // Ensure minimum height
          
          // Check if we need a new page for this activity
          const activityHeight = contentHeight + 10; // Add some padding
          if (y + activityHeight > doc.internal.pageSize.getHeight() - 30) {
            doc.addPage();
            y = margin + 40; // Leave space for header
            y = await addHeader(doc, idx + 1, day.label);
            y += 10;
          }
          
          // Set the startY after any potential page break
          const startY = y;
          
          // Draw time circle (left side)
          doc.setFillColor(10, 110, 156);
          doc.circle(margin + 15, startY + (contentHeight / 2), circleRadius, 'F');
          
          // Add time text (centered in circle)
          if (item.time) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(255, 255, 255);
            const timeText = item.time.replace(':', '\n');
            const timeY = startY + (contentHeight / 2) - 2;
            doc.text(timeText, margin + 15, timeY, { align: 'center' });
          }

          // Draw card background (light gray with subtle border)
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(230, 230, 230);
          doc.roundedRect(cardLeftMargin, startY, contentWidth - 30, contentHeight, 4, 4, 'FD');
          
          // Add connecting line from circle to card
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(1);
          doc.line(
            margin + 15 + circleRadius, 
            startY + (contentHeight / 2), 
            cardLeftMargin, 
            startY + (contentHeight / 2)
          );
          
          // Reset text styles
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.setTextColor(60, 60, 60);
          
          let contentY = startY + 12;

          // Title (bold, larger)
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(10, 110, 156); // Primary blue
          doc.text(item.title, cardLeftMargin + 10, contentY);
          
          // Description (under title)
          if (item.desc) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(90, 90, 90);
            const splitDesc = doc.splitTextToSize(item.desc, contentWidth - 60);
            const descToShow = splitDesc.slice(0, 2); // Max 2 lines
            doc.text(descToShow, cardLeftMargin + 10, contentY + 7);
            contentY += (descToShow.length * 4) + 4;
          }
          
          // Location (with pin icon)
          if (item.address) {
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.setFont('helvetica', 'normal');
            doc.text(`📍 ${item.address}`, cardLeftMargin + 10, contentY + 6);
            contentY += 5;
          }
          
          // Add subtle divider line
          doc.setDrawColor(240, 240, 240);
          doc.setLineWidth(0.5);
          doc.line(cardLeftMargin + 10, startY + contentHeight - 12, cardLeftMargin + contentWidth - 40, startY + contentHeight - 12);
          
          // Add category tags at bottom
          if (item.categories?.length > 0) {
            doc.setFontSize(7);
            let tagX = cardLeftMargin + 10;
            const tagY = startY + contentHeight - 8;
            
            item.categories.forEach((cat, i) => {
              const textWidth = doc.getTextWidth(cat) + 8;
              
              // Draw tag background
              doc.setFillColor(240, 240, 240);
              doc.roundedRect(tagX, tagY, textWidth, 6, 3, 3, 'F');
              
              // Draw tag text
              doc.setTextColor(100, 100, 100);
              doc.text(cat, tagX + 4, tagY + 4.5);
              
              tagX += textWidth + 4; // 4px between tags
              
              // Move to next line if not enough space
              if (tagX > cardLeftMargin + contentWidth - 50 && i < item.categories.length - 1) {
                tagX = cardLeftMargin + 10;
                contentY += 8;
              }
            });
          }

          y += contentHeight + 18;
        }
      }
      
      // Add footer to each page
      addFooter(doc);
    }
    
    // Add footer to all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      addFooter(doc);
    }
    
    // Save the PDF with a nice filename
    doc.save(`Portugal-Day-Trip-Itinerary-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportExcel = () => {
    // Define columns
    const header = ['Day', 'City', 'Weather', 'Transport', 'Distance', 'Duration', 'Slot', 'Activity', 'Notes'];
    const rows = [];
    days.forEach(day => {
      if (!day) return;
      const weatherText = day.weather ? `${day.weather.description} (${day.weather.min}°C - ${day.weather.max}°C)` : '';
      const mode = day.travel?.mode || '';
      const dist = day.travel?.distance ? `${day.travel.distance} km` : '';
      const dur = day.travel?.duration ? `${day.travel.duration} mins` : '';
      const slots = day.slots || { morning: day.items || [] };
      Object.entries(slots).forEach(([slotKey, items]) => {
        if (!items?.length) return;
        items.forEach(item => {
          rows.push({
            Day: day.label,
            City: day.city,
            Weather: weatherText,
            Transport: mode,
            Distance: dist,
            Duration: dur,
            Slot: slotKey.charAt(0).toUpperCase() + slotKey.slice(1),
            Activity: item.title || '',
            Notes: item.notes || ''
          });
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows, { header });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Itinerary');
    XLSX.writeFile(wb, 'itinerary.xlsx');
  };

  const shareViaEmail = () => {
    let body = "Portugal Trip Itinerary%0D%0A%0D%0A";
    
    days.forEach(day => {
      if (!day) return;
      
      body += `${day.label}%0D%0A`;
      
      // Handle both old and new format
      const slots = day.slots || { morning: day.items || [] };
      Object.entries(slots).forEach(([slot, items]) => {
        if (!items || !Array.isArray(items)) return;
        
        items.forEach(item => {
          if (!item || !item.title) return;
          body += `- ${item.title}%0D%0A`;
        });
        body += "%0D%0A";
      });
    });
    
    window.location.href = `mailto:?subject=Portugal Trip Itinerary&body=${encodeURIComponent(body)}`;
  };

  // Generate shareable link to invite collaborators
  const copyShareLink = () => {
    const encoded = btoa(JSON.stringify(days));
    const url = `${window.location.origin}${window.location.pathname}?share=${encoded}`;
    navigator.clipboard.writeText(url).then(() => alert('Share link copied to clipboard!'));
  };

  // Export a 'story mode' image of the itinerary
  const exportStoryImage = () => {
    let el = document.getElementById('itinerary-wrapper');
    if (!el) {
      console.warn('itinerary-wrapper not found, capturing full page instead.');
      el = document.body;
    }
    html2canvas(el).then(canvas => {
      const link = document.createElement('a');
      link.download = 'itinerary-story.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    });
  };

  return (
    <>
      <Banner>
        <h2>
          <span>Share Your Portugal Adventure</span>
          <span className="divider">•</span>
          <span>Export Options</span>
        </h2>
        <span className="promo-text">
          <span className="highlight">Choose format:</span> Save or share your itinerary in different formats
        </span>
      </Banner>
      <Toolbar role="toolbar">
      <ActionButton bg="#2563eb" hoverBg="#1e40af" onClick={exportPDF}>Export PDF</ActionButton>
      <ActionButton bg="#22c55e" hoverBg="#15803d" onClick={exportExcel}>Export Excel</ActionButton>
      <ActionButton bg="#f59e42" hoverBg="#d97706" onClick={shareViaEmail}>Share via Email</ActionButton>
      <ActionButton bg="#fb7185" hoverBg="#be123c" onClick={copyShareLink}>Copy Share Link</ActionButton>
      <ActionButton bg="#8b5cf6" hoverBg="#6d28d9" onClick={exportStoryImage}>Export Story Image</ActionButton>

      {showReviewModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '1rem',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h2 style={{ color: '#2563eb', marginBottom: '1rem' }}>Please Review Us!</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              We would love to hear about your experience with our trip planner! 
              Your feedback helps us improve our service for future travelers.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #2563eb',
                  backgroundColor: 'white',
                  color: '#2563eb',
                  borderRadius: '0.5rem',
                  cursor: 'pointer'
                }}
                onClick={handleReviewSkip}
              >
                Skip
              </button>
              <a
                href="https://www.tripadvisor.com/Attraction_Review-g189149-d123456789.html"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleReviewSubmit}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  borderRadius: '0.5rem',
                  textDecoration: 'none',
                  cursor: 'pointer'
                }}
              >
                Write Review
              </a>
            </div>
          </div>
        </div>
      )}
      </Toolbar>
    </>
  );
}
